import { AuthService } from './authService';
import { getSessionConfig, SessionConfig } from '../config/sessionConfig';

/**
 * SessionService manages user session based on activity
 * - Tracks user activity
 * - Refreshes tokens automatically
 * - Handles session expiration
 */
export class SessionService {
  private authService: AuthService;
  private config: SessionConfig;
  
  private activityTimer: NodeJS.Timeout | null = null;
  private sessionTimer: NodeJS.Timeout | null = null;
  private warningTimer: NodeJS.Timeout | null = null;
  private lastActivityUpdate: number = 0;
  private sessionStartTime: number = Date.now();
  
  private onSessionWarning: ((remainingTime: number) => void) | null = null;
  private onSessionExpired: (() => void) | null = null;
  
  constructor(
    authService: AuthService, 
    config: Partial<SessionConfig> = {}
  ) {
    this.authService = authService;
    
    // Get default configuration and override with provided values
    const defaultConfig = getSessionConfig();
    this.config = {
      ...defaultConfig,
      ...config
    };
    
    this.setupActivityListeners();
    this.setupSessionTimer();
    
    // Start the inactivity timer immediately
    this.resetInactivityTimer();
  }
  
  /**
   * Set up event listeners to track user activity
   */
  private setupActivityListeners(): void {
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, () => this.handleUserActivity());
    });
  }
  
  /**
   * Set up timer for maximum session duration
   */
  private setupSessionTimer(): void {
    this.sessionStartTime = Date.now();
    this.sessionTimer = setTimeout(() => {
      this.handleSessionExpiration();
    }, this.config.maxSessionDuration);
  }
  
  /**
   * Handle user activity
   * - Reset inactivity timer
   * - Update server-side activity timestamp (throttled)
   */
  private handleUserActivity(): void {
    this.resetInactivityTimer();
    this.updateServerActivity();
  }
  
  /**
   * Reset the inactivity timer
   */
  private resetInactivityTimer(): void {
    // Clear any existing timers
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    
    const warningDelay = this.config.inactivityTimeout - this.config.warningPeriod;
    
    // Set new inactivity timer
    this.activityTimer = setTimeout(() => {
      // Set warning timer
      this.warningTimer = setTimeout(() => {
        this.handleSessionExpiration();
      }, this.config.warningPeriod);
      
      // Trigger warning callback
      if (this.onSessionWarning) {
        this.onSessionWarning(this.config.warningPeriod);
      }
    }, warningDelay);
  }
  
  /**
   * Update activity timestamp on server (throttled)
   */
  private updateServerActivity(): void {
    // Throttle to avoid too many requests (once per minute)
    if (this.lastActivityUpdate && Date.now() - this.lastActivityUpdate < 60000) {
      return;
    }
    
    this.lastActivityUpdate = Date.now();
    this.authService.updateActivity().catch(error => {
      console.error('Failed to update activity:', error);
    });
  }
  
  /**
   * Handle session expiration
   */
  private handleSessionExpiration(): void {
    this.cleanup();
    
    if (this.onSessionExpired) {
      this.onSessionExpired();
    } else {
      // Default behavior - logout
      this.authService.logout();
      // Reload page to ensure clean state
      window.location.href = '/login';
    }
  }
  
  /**
   * Clean up all timers
   */
  public cleanup(): void {
    if (this.activityTimer) {
      clearTimeout(this.activityTimer);
      this.activityTimer = null;
    }
    
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }
  
  /**
   * Set callback for session warning
   */
  public setSessionWarningCallback(callback: (remainingTime: number) => void): void {
    this.onSessionWarning = callback;
  }
  
  /**
   * Set callback for session expiration
   */
  public setSessionExpiredCallback(callback: () => void): void {
    this.onSessionExpired = callback;
  }
  
  /**
   * Manually refresh the token
   */
  public async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }
      
      const response = await this.authService.refreshToken(refreshToken);
      
      // Reset timers
      this.resetInactivityTimer();
      
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }
  
  /**
   * Continue session (called when user responds to warning)
   */
  public continueSession(): void {
    this.handleUserActivity();
  }
}
